import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, BadgeCheck, Cable, CalendarClock, Database as DatabaseIcon, FileBarChart, Gauge as GaugeIcon, LayoutDashboard, LifeBuoy, LogOut, Menu, Moon, Network, Radio,
  Settings as SettingsIcon, Sun, Users, UserCog, UserSquare2, Volume2, VolumeX, Wrench, X, Router as RouterIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, BASE } from "@/lib/api";
import { playChime, soundEnabled, toggleSound } from "@/lib/sound";
import { Button } from "@/components/ui/button";
import { NotifBar } from "@/components/NotifBar";
import { InstallButton, NotifBanner, NotifEnableButton, syncSwToken, useAutoPush, useSaldoAlarm } from "@/lib/pwa";

const NAV = [
  { to: "/dashboard", label: "Dasbor", icon: LayoutDashboard, roles: ["superadmin", "admin", "teknisi"] },
  { to: "/hotspot", label: "Hotspot", icon: Radio, roles: ["superadmin", "admin"] },
  { to: "/ftth", label: "FTTH / PPPoE", icon: Cable, roles: ["superadmin", "admin", "teknisi"] },
  { to: "/pelanggan", label: "Pelanggan FTTH", icon: UserSquare2, roles: ["superadmin", "admin", "teknisi"] },
  { to: "/tiket", label: "Tiket Gangguan", icon: LifeBuoy, roles: ["superadmin", "admin", "teknisi"] },
  { to: "/infrastruktur", label: "Infrastruktur", icon: Network, roles: ["superadmin", "admin", "teknisi"] },
  { to: "/mitra", label: "Mitra", icon: Users, roles: ["superadmin", "admin"] },
  { to: "/pengguna", label: "User", icon: UserSquare2, roles: ["superadmin", "admin"] },
  { to: "/teknisi", label: "Teknisi", icon: Wrench, roles: ["superadmin", "admin"] },
  { to: "/admin-akun", label: "Admin", icon: GaugeIcon, roles: ["superadmin"] },
  { to: "/member", label: "Member & Langganan", icon: BadgeCheck, roles: ["superadmin"] },
  { to: "/laporan", label: "Laporan & Audit", icon: FileBarChart, roles: ["superadmin", "admin"] },
  { to: "/setting", label: "Setting", icon: SettingsIcon, roles: ["superadmin", "admin"] },
  { to: "/backup", label: "Backup & Restore", icon: DatabaseIcon, roles: ["superadmin", "admin"] },
  { to: "/akun", label: "Akun Saya", icon: UserCog, roles: ["superadmin", "admin", "teknisi"] },
  { to: "/langganan", label: "Langganan Saya", icon: CalendarClock, roles: ["admin"] },
];

export default function Shell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [notifTick, setNotifTick] = useState(0);
  const alarm = useSaldoAlarm();
  const [sound, setSound] = useState(soundEnabled());
  const [brand, setBrand] = useState({ logo: "", app_name: "ALPHA BILL" });

  useEffect(() => {
    api.get("/public/landing")
      .then(({ data }) => setBrand({ logo: data.logo || "", app_name: data.app_name || "ALPHA BILL" }))
      .catch((e) => console.warn("shell: gagal memuat logo", e?.message || e));
  }, []);
  useAutoPush();

  useEffect(() => {
    syncSwToken();
    const onMsg = (e) => {
      if (e.data?.type !== "deposit_handled") return;
      alarm.stop();
      setNotifTick((n) => n + 1);
      toast[e.data.ok ? "success" : "error"](
        e.data.ok
          ? `Permintaan saldo ${e.data.action === "approve" ? "disetujui" : "ditolak"} dari notifikasi`
          : "Aksi dari notifikasi gagal, coba dari aplikasi",
      );
    };
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const sub = (user && user !== true && user.subscription) || {};
  const subWarn = !!sub.managed && !sub.expired && typeof sub.days_left === "number" && sub.days_left <= 7;

  useEffect(() => {
    const t = localStorage.getItem("ab_token");
    const url = BASE.replace(/^http/, "ws") + "/api/ws" + (t ? `?token=${encodeURIComponent(t)}` : "");
    let ws;
    let closed = false;
    try {
      ws = new WebSocket(url);
      ws.onerror = () => {};
      ws.onmessage = (ev) => {
        const { event, data } = JSON.parse(ev.data);
        if (event === "hotspot_login") { playChime("success"); toast.success(`Hotspot login: ${data.username}`, { description: data.ip }); }
        if (event === "deposit_request") {
          setNotifTick((n) => n + 1);
          alarm.start(data);
        }
        if (event === "ticket_new" || event === "ticket_update") setNotifTick((n) => n + 1);
        if (event === "router_status") { playChime(data.online ? "success" : "error"); toast[data.online ? "success" : "error"](`Router ${data.router} ${data.online ? "online" : "offline"}`); }
        if (event === "vouchers_generated") { playChime("success"); toast.success(`${data.count} voucher dibuat ${data.owner ? "oleh " + data.owner : ""}`); }
        if (event === "ftth_isolir") { playChime(data.status === "isolir" ? "warning" : "success"); toast[data.status === "isolir" ? "warning" : "success"](`FTTH ${data.name}: ${data.status}`, { description: data.by ? `oleh ${data.by}` : undefined }); }
        if (event === "ftth_notif") { playChime("warning"); toast.info(`Tagihan ${data.name}: ${data.type}`); }
        if (event === "ppp_offline") { playChime("error"); toast.warning(`PPPoE tidak aktif: ${data.name}`, { description: `secret ${data.secret} terputus` }); }
        if (event === "ticket_new") { playChime("warning"); toast.warning(`Laporan gangguan baru: ${data.customer}`, { description: `${data.code} · ${data.category || data.priority}` }); }
        if (event === "ticket_update") { playChime("info"); toast.info(`Tiket ${data.code} → ${data.status}`); }
        if (event === "ticket_assigned") { playChime("info"); toast.info(`Tiket ${data.code} ditugaskan ke ${data.teknisi || "-"}`); }
      };
    } catch (e) {
      console.error("koneksi WebSocket gagal:", e);
    }
    return () => {
      closed = true;
      if (!ws) return;
      if (ws.readyState === WebSocket.OPEN) ws.close();
      else ws.onopen = () => closed && ws.close();
    };
  }, []);

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="grain min-h-screen bg-background">
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-card/80 glass px-4 py-6 transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="flex shrink-0 items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3">
            {brand.logo ? (
              <img src={brand.logo} alt={brand.app_name} className="h-9 w-9 rounded-xl object-contain" data-testid="shell-logo" />
            ) : (
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
                <RouterIcon className="h-4 w-4 text-white" />
              </div>
            )}
            <div>
              <p className="font-display text-sm font-semibold tracking-tight">{brand.app_name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{user?.role}</p>
            </div>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="mt-8 flex-1 space-y-1 overflow-y-auto pb-2 pr-1" data-testid="sidebar-nav">
          {items.map(({ to, label, icon: Icon }) => {
            const active = loc.pathname.startsWith(to);
            return (
              <Link
                key={to} to={to} onClick={() => setOpen(false)}
                data-testid={`nav-${to.replace("/", "")}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-200 ${
                  active
                    ? "bg-gradient-to-r from-cyan-500/15 to-violet-600/15 text-foreground border border-violet-500/30"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground border border-transparent"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-3 shrink-0 space-y-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={doLogout} data-testid="logout-btn" className="w-full justify-start rounded-xl text-sm text-muted-foreground hover:text-destructive">
            <LogOut className="mr-2 h-4 w-4" /> Keluar
          </Button>
          <div
            className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 font-mono text-[10px] text-muted-foreground"
            data-testid="live-indicator"
          >
            <span className="flex items-center gap-1.5"><Activity className="h-3 w-3 text-emerald-400" /> live</span>
            <span className="truncate">
              {clock.toLocaleTimeString("id-ID")} · {clock.toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}
            </span>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-border glass px-4 py-3 sm:gap-4 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <p className="font-display text-base font-semibold tracking-tight">
                {items.find((i) => loc.pathname.startsWith(i.to))?.label || "Dasbor"}
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{user?.name || user?.username}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <InstallButton />
            <NotifEnableButton />
            <NotifBar refreshKey={notifTick} />
            <Button
              variant="ghost" size="icon" data-testid="sound-toggle" className="rounded-full"
              title={sound ? "Matikan suara notifikasi" : "Nyalakan suara notifikasi"}
              onClick={() => { const on = toggleSound(); setSound(on); if (on) playChime("info"); }}
            >
              {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-full">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main className="relative z-10 px-5 py-8 lg:px-10 lg:py-10" data-testid="main-content">
          <NotifBanner />
          {subWarn && (
            <div
              className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/5 px-4 py-3"
              data-testid="sub-warning-banner"
            >
              <p className="text-xs text-amber-300">
                Masa sewa dasbor Anda berakhir dalam <b>{sub.days_left} hari</b> ({new Date(sub.expires_at).toLocaleDateString("id-ID")}).
                Perpanjang sekarang agar akses tidak terputus.
              </p>
              <Button asChild size="sm" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
                <Link to="/langganan" data-testid="sub-warning-cta">Perpanjang</Link>
              </Button>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
